import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button, Divider, Typography } from '@mui/material'
import ApplicationCard from '@norstella/nxp-sso-web/components/ApplicationCard'
import { useAppContext } from '@norstella/nxp-sso-web/components/AppContextProvider'
import { IBrandingPageSection, useBranding } from '../hooks/useBranding'
import { KNOWN_PAGES } from '@norstella/nxp-sso-web/utils/pages'
import parse from 'html-react-parser'

// TODO: refactor to reduce complexity
// eslint-disable-next-line complexity
export const VerifyEmail = () => {
  const { user, apiHooks } = useAppContext()
  const [forms, branding] = useBranding()
  const [isButtonDirty, setIsButtonDirty] = useState(false)
  const [isButtonVisible, setIsButtonVisible] = useState(true)
  const [searchParams] = useSearchParams()
  const [verifyEmail] = apiHooks.user.useVerifyEmail()

  const isVerificationFailed = searchParams.get('success') === 'false'
  const message = searchParams.get('message')
  const isLinkExpired = message === 'Access expired.'
  const isVerificationSuccess = !isVerificationFailed

  const pageOptions = branding?.[KNOWN_PAGES.EMAIL_VERIFY] as IBrandingPageSection
  const resendBtnText = pageOptions?.resendEmailBtnText ?? 'Resend verification email'

  const headerMeta = useMemo(() => {
    const meta = isVerificationFailed ? forms.verifyEmailError : forms.verifyEmail

    if (pageOptions?.title) {
      meta.header.text = pageOptions.title
    }

    if (isLinkExpired) {
      meta.header.text = 'Link Expired'
    }

    return meta
  }, [isVerificationFailed, isLinkExpired, forms, pageOptions])

  const verificationContentCustom = useMemo(() => {
    if (!pageOptions?.description) {
      return null
    }
    const description = pageOptions.description.replace('{{user.email}}', user?.email ?? '')
    return parse(`<div>${description}</div>`, { trim: true }) as JSX.Element
  }, [pageOptions?.description, user?.email])

  // TODO: refactor to reduce complexity
  // eslint-disable-next-line complexity
  const { main: verificationContentMain, secondary: verificationContentSecondary } = useMemo(() => {
    if (!user) {
      return {
        main: null,
        secondary: null,
      }
    }

    if (isVerificationFailed && isLinkExpired) {
      return {
        main: (
          <>
            <Typography component="p">This link is expired for security reasons.</Typography>
            <Typography component="p">Please click on the button to send a new email.</Typography>
          </>
        ),
        secondary: <></>,
      }
    }

    if (isVerificationFailed) {
      if (message) {
        return {
          main: <Typography component="p">{message}</Typography>,
          secondary: <></>,
        }
      }
      return {
        main: (
          <>
            <Typography component="p">Your email address could not be verified.</Typography>
            <Typography component="p">Please click on the button to send a new email.</Typography>
          </>
        ),
        secondary: <></>,
      }
    }

    if (isVerificationSuccess) {
      if (verificationContentCustom) {
        return {
          main: verificationContentCustom,
          secondary: <></>,
        }
      }
      return {
        main: (
          <>
            <Typography component="p">
              A verification email has been sent to the email address {user.email}.
            </Typography>
            <Typography component="p">
              Use the verification link in the email to activate and complete the sign up process.
            </Typography>
          </>
        ),
        secondary: (
          <>
            <Typography variant="h2" component="h2">No verification email?</Typography>
            <Typography component="p">
              If you did not receive the verification email, use the button below to request a new one.
            </Typography>
          </>
        ),
      }
    }

    return { main: null, secondary: null }
  }, [user, isVerificationFailed, isVerificationSuccess, message, verificationContentCustom, isLinkExpired])

  useMemo(() => {
    if (isVerificationFailed && !verificationContentMain) {
      setIsButtonVisible(false)
    }
  }, [isVerificationFailed, verificationContentMain])

  const onClickVerify = () => {
    setIsButtonDirty(true)
    verifyEmail()
  }

  if (!user) {
    return null
  }

  return (
    <ApplicationCard
      header={headerMeta.header}
      description={headerMeta.description}
      pageName={KNOWN_PAGES.EMAIL_VERIFY}
    >
      {verificationContentMain}
      {!verificationContentCustom && <Divider />}
      {!verificationContentCustom && verificationContentSecondary}
      {isButtonVisible && (
        <>
          <div className="buttons-container align-center">
            <Button
              data-testid="verify-email-button"
              disabled={isButtonDirty}
              type="button"
              variant="contained"
              color="primary"
              onClick={onClickVerify}
            >
              {resendBtnText}
            </Button>
          </div>
          <Divider />
        </>
      )}
      {headerMeta.footer?.text && (headerMeta.footer?.linkUrl || headerMeta.footer?.emailAddress) && (
        <>
          <Typography variant="h2" component="h2">Need help?</Typography>
          <Typography component="p">
            {headerMeta.footer.text}
            {headerMeta.footer.linkUrl && headerMeta.footer.linkText && (
              <>
                <span>&nbsp;</span>
                <a
                  href={headerMeta.footer.linkUrl}
                  target="_blank"
                  rel="noopener nofollow noreferrer"
                >
                  {headerMeta.footer.linkText}
                </a>
              </>
            )}
            {headerMeta.footer.emailAddress && headerMeta.footer.emailText && (
              <>
                <span>&nbsp;</span>
                <a href={`mailto:${headerMeta.footer.emailAddress}`}>
                  {headerMeta.footer.emailText}
                </a>
              </>
            )}
          </Typography>
        </>
      )}
    </ApplicationCard>
  )
}
